'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';

type ScrollState = {
  scrolled: boolean;
  onDarkBg: boolean;
};

const ScrollContext = createContext<ScrollState>({ scrolled: false, onDarkBg: true });

export function ScrollProvider({ children }: { children: React.ReactNode }) {
  const [scrolled, setScrolled] = useState(false);
  const [onDarkBg, setOnDarkBg] = useState(true);

  // Cache the dark-section list. We only need to refresh it on resize /
  // route change — re-querying the DOM on every scroll event allocates a
  // fresh NodeList + DOMRect array and was a major source of GC churn.
  const sectionsRef = useRef<HTMLElement[]>([]);
  const refreshSections = () => {
    sectionsRef.current = Array.from(
      document.querySelectorAll<HTMLElement>('[data-header-dark]'),
    );
  };

  useEffect(() => {
    const HEADER_H = 30;
    let rafId = 0;
    let scheduled = false;

    const measure = () => {
      scheduled = false;
      const nextScrolled = window.scrollY > 10;
      setScrolled(prev => (prev === nextScrolled ? prev : nextScrolled));
      const sections = sectionsRef.current;
      let isOverDark = false;
      for (let i = 0; i < sections.length; i++) {
        const r = sections[i].getBoundingClientRect();
        if (r.top < HEADER_H && r.bottom > 20) { isOverDark = true; break; }
      }
      setOnDarkBg(prev => (prev === isOverDark ? prev : isOverDark));
    };

    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      rafId = requestAnimationFrame(measure);
    };

    refreshSections();
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', refreshSections);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', refreshSections);
    };
  }, []);

  return (
    <ScrollContext.Provider value={{ scrolled, onDarkBg }}>
      {children}
    </ScrollContext.Provider>
  );
}

export const useScroll = () => useContext(ScrollContext);
